import { batch } from '@legendapp/state'
import { reactive, ShapeWith$, Show, useObservable } from '@legendapp/state/react'
import { AnimatePresence, motion } from 'framer-motion'
import { FC, useLayoutEffect, useRef } from 'react'

import { route$ } from '~in-app/legend-state'
import { router } from '~in-app/router'

interface ProgressBarProps {
  value: number
  className?: string
}

const ProgressBar: FC<ShapeWith$<ProgressBarProps>> = reactive(({ value, className }) => {
  return (
    <motion.div
      key="progress-bar"
      animate={{ x: 0 }}
      className={`absolute z-30 h-1 w-full overflow-hidden bg-background ${className}`}
      exit={{ x: '100%' }}
      initial={{ x: 0 }}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
    >
      <motion.div
        animate={{ width: `${value}%` }}
        className="h-full bg-blue-500"
        initial={{ width: 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      />
    </motion.div>
  )
})

export const RouteNavigationListener = () => {
  // eslint-disable-next-line no-undef
  const timerRef = useRef<Timer>()
  const state$ = useObservable({
    progress: 0,
    resolved: false,
    incProgress: (value: number) => {
      state$.progress.set(value)
    },
  })

  useLayoutEffect(() => {
    const onBeforeNavigate = router.subscribe('onBeforeNavigate', (event) => {
      clearTimeout(timerRef?.current)
      batch(() => {
        state$.resolved.set(false)
        state$.incProgress(25)
      })
    })

    const onBeforeLoad = router.subscribe('onBeforeLoad', () => {
      state$.incProgress(50)
    })

    const onLoad = router.subscribe('onLoad', () => {
      state$.incProgress(75)
    })

    const onResolved = router.subscribe('onResolved', (event) => {
      state$.incProgress(100)
      if (event.fromLocation) {
        route$.registerPage(event.fromLocation.pathname)
        route$.tracePath(event.fromLocation.pathname)
      }
      timerRef.current = setTimeout(() => {
        state$.resolved.set(true)
      }, 500)
    })

    return () => {
      onBeforeNavigate()
      onBeforeLoad()
      onLoad()
      onResolved()
    }
  }, [state$])

  return (
    <Show
      if={() => state$.progress.get() > 0 && state$.resolved.get() === false}
      wrap={AnimatePresence}
    >
      {() => <ProgressBar $value={state$.progress} />}
    </Show>
  )
}
